import React, { memo, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Search, FolderPlus, Grid3X3, List } from "lucide-react";
import { cn } from "../../utils/cn";
import { ProjectCard } from "../cards/ProjectCard";
import { Project, User, Epic } from "../../types";
import { calculateProjectRoleCounts } from "../../utils/teamMetrics";

interface ProjectListViewProps {
    projects: Project[];
    epics: Epic[];
    selectedProjectId: string | null;
    onSelectProject?: (id: string) => void;
    onAddProject?: () => void;
    onEditProject?: (id: string, e: React.MouseEvent) => void;
    onDeleteProject?: (id: string, e: React.MouseEvent) => void;
    search?: string;
    onSearchChange?: (query: string) => void;
    users?: User[];
}

/**
 * ProjectListView - Grid/List view of all projects
 */
export const ProjectListView = memo(function ProjectListView({
    projects,
    epics,
    selectedProjectId,
    onSelectProject,
    onAddProject,
    onEditProject,
    onDeleteProject,
    search = "",
    onSearchChange,
    users = [],
}: ProjectListViewProps) {
    const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

    // Filter projects by search
    const filteredProjects = useMemo(() => {
        if (!search) return projects;
        const searchLower = search.toLowerCase();
        return projects.filter(p =>
            p.name.toLowerCase().includes(searchLower) ||
            p.description?.toLowerCase().includes(searchLower) ||
            p.owner?.toLowerCase().includes(searchLower)
        );
    }, [projects, search]);

    // Stats
    const stats = useMemo(() => ({
        total: projects.length,
        active: projects.filter(p => p.status === "active").length,
        totalEpics: projects.reduce((sum, p) => sum + (p.epicCount || 0), 0),
    }), [projects]);

    // Calculate role counts per project
    const projectRoleCounts = useMemo(() => {
        const counts: Record<string, { pmCount: number; ownerCount: number; architectCount: number }> = {};
        projects.forEach(project => {
            const projectEpics = epics.filter(e => String(e.projectId) === String(project.id));
            counts[project.id] = calculateProjectRoleCounts(projectEpics, project, users);
        });
        return counts;
    }, [projects, epics, users]);

    return (
        <div className="flex-1 flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-siemens-deep-blue/5 dark:border-siemens-divider bg-white/50 dark:bg-siemens-elevation-base-1/50 backdrop-blur-sm">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-xl font-black text-siemens-deep-blue dark:text-white">
                            ZPS Innovation
                        </h1>
                        <p className="text-sm text-siemens-text-soft">
                            {stats.total} projects • {stats.active} active • {stats.totalEpics} epics
                        </p>
                    </div>
                    <button
                        onClick={onAddProject}
                        className="h-10 px-4 bg-siemens-petrol text-white rounded-lg flex items-center gap-2 hover:bg-siemens-petrol-dark transition-colors shadow-sm"
                    >
                        <FolderPlus className="w-4 h-4" />
                        <span className="text-sm font-semibold">New Project</span>
                    </button>
                </div>

                {/* Search and View Toggle */}
                <div className="flex items-center gap-3">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-siemens-text-soft" />
                        <input
                            type="text"
                            placeholder="Search projects..."
                            value={search}
                            onChange={(e) => onSearchChange?.(e.target.value)}
                            className="w-full h-10 pl-10 pr-4 bg-slate-100 dark:bg-siemens-elevation-base-1 rounded-lg text-sm text-siemens-deep-blue dark:text-white placeholder:text-siemens-text-soft border-none focus:ring-2 focus:ring-siemens-petrol/30"
                        />
                    </div>
                    <div className="flex items-center gap-1 p-1 bg-slate-100 dark:bg-siemens-elevation-base-1 rounded-lg">
                        <button
                            onClick={() => setViewMode("grid")}
                            className={cn(
                                "p-2 rounded-md transition-colors",
                                viewMode === "grid"
                                    ? "bg-white dark:bg-siemens-elevation-selected text-siemens-petrol shadow-sm"
                                    : "text-siemens-text-soft hover:text-siemens-deep-blue dark:hover:text-white"
                            )}
                        >
                            <Grid3X3 className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setViewMode("list")}
                            className={cn(
                                "p-2 rounded-md transition-colors",
                                viewMode === "list"
                                    ? "bg-white dark:bg-siemens-elevation-selected text-siemens-petrol shadow-sm"
                                    : "text-siemens-text-soft hover:text-siemens-deep-blue dark:hover:text-white"
                            )}
                        >
                            <List className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Project Grid/List */}
            <div className="flex-1 overflow-y-auto p-6">
                {filteredProjects.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-center">
                        <FolderPlus className="w-12 h-12 text-siemens-text-soft mb-4" />
                        <h3 className="text-lg font-bold text-siemens-deep-blue dark:text-white mb-2">
                            No projects found
                        </h3>
                        <p className="text-sm text-siemens-text-soft mb-4">
                            {search ? "Try a different search term" : "Create your first project to get started"}
                        </p>
                        {!search && (
                            <button
                                onClick={onAddProject}
                                className="h-10 px-4 bg-siemens-petrol text-white rounded-lg flex items-center gap-2 hover:bg-siemens-petrol-dark transition-colors"
                            >
                                <Plus className="w-4 h-4" />
                                <span className="text-sm font-semibold">New Project</span>
                            </button>
                        )}
                    </div>
                ) : (
                    <motion.div
                        layout
                        className={cn(
                            viewMode === "grid"
                                ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                                : "flex flex-col gap-3"
                        )}
                    >
                        <AnimatePresence mode="popLayout">
                            {filteredProjects.map((project) => (
                                <motion.div
                                    key={project.id}
                                    layout
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                >
                                    <ProjectCard
                                        project={project}
                                        selected={project.id === selectedProjectId}
                                        onClick={() => onSelectProject?.(project.id)}
                                        onEdit={onEditProject}
                                        onDelete={onDeleteProject}
                                        laneDistribution={(project as any).laneDistribution}
                                        users={users}
                                        roleCounts={projectRoleCounts[project.id]}
                                    />
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </motion.div>
                )}
            </div>
        </div>
    );
});

export default ProjectListView;
